// eslint-disable-next-line no-restricted-imports
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  Flex,
  Notice,
  NumberInput,
  Select,
  Text,
  useToasts,
} from '@applyboard/crystal-ui';
import { PlusOutlineIcon } from '@applyboard/ui-icons';
import { useCurrentUser, useEventTracker, useFeatureDecision } from '@applyboard/ui-utils';
import { useAgency } from '../../data/agency/agency';
import { getApplyShopConfig, useGetProducts } from '../../data/applyshopStore';
import { useCountries } from '../../data/countries/useCountries';
import {
  getBestAvailablePrices,
  getTestCardPricing,
  isProductSoldOut,
} from '../../utils/formatPrice';
import { getApplyButtonLink } from '../VasHub/ServiceListings/utils';
import normalizeVoucherType from './normalizeVoucherType';
import { TestVoucherTypes, VOUCHER_TYPE_LABEL_MAP } from './types';

interface PurchaseTestVouchersModalProps {
  buttonSize?: 'sm' | 'md' | 'lg';
  buttonWidth?: 'hug' | 'fill';
  buttonText?: string;
}

export function PurchaseTestVouchersModal({
  buttonSize = 'md',
  buttonWidth = 'hug',
  buttonText = 'Purchase Test Vouchers',
}: PurchaseTestVouchersModalProps) {
  const [open, setOpen] = useState(false);
  const [testLocation, setTestLocation] = useState<string>('');
  const [testType, setTestType] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('1');
  const [currentUser] = useCurrentUser();
  const [decisionEngine] = useFeatureDecision();
  const toast = useToasts();
  const { trackEvent } = useEventTracker();

  // Get agency information to determine store selection (same pattern as VasHub)
  const agentId = currentUser?.relationships?.agent_information?.data?.id as string;
  const userId = currentUser?.id ?? '';
  const { agency, isLoadingAgency, agencyError } = useAgency(agentId, {
    include: 'agency_employee.address,master_agency.agency_employee,logo_attachment',
  });

  // Get countries for the nationality dropdown (same as ServiceListings.tsx)
  const { isLoadingCountries, countries } = useCountries();

  const agencyCountry = agency?.agencyEmployee?.address?.country ?? '';
  const isNepalShopifyStoreEnabled =
    decisionEngine?.isFeatureEnabled('vas_nepal_shoppify_store') ?? false;

  // Convert country codes to country names for variant matching (same as ServiceListings.tsx)
  const testLocationName = useMemo(() => {
    return testLocation
      ? countries.find(c => c.code === testLocation)?.name || testLocation
      : undefined;
  }, [testLocation, countries]);

  // Get products from the appropriate store based on test location
  const { products, productsError, isProductsLoading } = useGetProducts(
    {
      applyShopConfig: getApplyShopConfig(
        testLocation || agencyCountry,
        isNepalShopifyStoreEnabled,
      ),
    },
    { enabled: Boolean(testLocation || agencyCountry) && Boolean(agentId) },
  );

  // Create country options for dropdown (same as ServiceListings.tsx)
  const countryOptions = useMemo(() => {
    return countries.map(country => ({
      value: country.code,
      label: country.name,
    }));
  }, [countries]);

  const isLoading = isLoadingAgency || isProductsLoading || isLoadingCountries;

  useEffect(() => {
    if (agencyError) {
      console.error('Failed to load agency details', agencyError);
      toast.negative(new Error('Failed to load agency details'));
    }
  }, [agencyError, toast]);

  useEffect(() => {
    if (productsError) {
      console.error('Failed to load products', productsError);
      toast.negative(new Error('Failed to load products'));
    }
  }, [productsError, toast]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setTestLocation('');
      setTestType('');
      setQuantity('1');
    }
  }, [open]);

  // Set default agency country on load (same as ServiceListings.tsx)
  useEffect(() => {
    if (agencyCountry && open) {
      setTestLocation(agencyCountry);
    }
  }, [agencyCountry, open]);

  // Find products using the same logic as LanguageTestCardsData.tsx
  const selectedProduct = useMemo(() => {
    if (!testType || !products) return undefined;

    switch (testType.toLowerCase()) {
      case 'pte':
        return products.find(product => product.handle.toLowerCase().includes('pte'));
      case 'toefl':
        return products.find(product => product.handle.toLowerCase().includes('toefl'));
      case 'duolingo':
        return products.find(
          product =>
            product.handle.toLowerCase().includes('det') ||
            product.handle.toLowerCase().includes('duolingo'),
        );
      case 'gre':
        return products.find(product => product.handle.toLowerCase().includes('gre'));
      default:
        return undefined;
    }
  }, [testType, products]);

  // Use the same pricing and availability logic as LanguageTestCardsData.tsx
  const productInfo = useMemo(() => {
    if (!selectedProduct || !testLocationName) {
      return { isAvailable: false, pricing: null, isSoldOut: true };
    }

    const priceData = getBestAvailablePrices(selectedProduct, [testLocationName]);
    const pricing = priceData
      ? getTestCardPricing(priceData.discountedAmount, priceData.fullAmount, priceData.currencyCode)
      : null;
    const isSoldOut = isProductSoldOut(selectedProduct, [testLocationName]);

    return {
      isAvailable: !isSoldOut && !!priceData,
      pricing,
      isSoldOut,
      priceData,
    };
  }, [selectedProduct, testLocationName]);

  // Use the same logic as LanguageTestCardsData.tsx for button states
  const hasRequiredSelections = testLocation && testType;
  const hasProduct = !!selectedProduct;
  const isSoldOut = productInfo.isSoldOut;
  const hasErrors = !!productsError;

  // Button should be disabled when: no product, has errors, or is sold out (same as LanguageTestCardsData.tsx)
  const isButtonDisabled = !hasProduct || hasErrors || isSoldOut || isLoading;

  const handleContinue = useCallback(() => {
    if (!selectedProduct || !testLocationName || isButtonDisabled) return;

    // Use the same logic as LanguageTestCardsData.tsx to get the cart link
    const applyButtonLink = getApplyButtonLink(
      selectedProduct,
      userId,
      testLocation || agencyCountry,
      isNepalShopifyStoreEnabled,
      [testLocationName],
    );

    if (applyButtonLink) {
      trackEvent({
        event: 'VAS - Vas Hub Page - Voucher Management - Purchase Test Voucher',
        data: {
          rp_id: userId,
          voucher_type: normalizeVoucherType(testType),
          country: testLocation || agencyCountry,
          quantity: quantity,
        },
      });
      // Replace the default ":1" with custom quantity in the cart link
      const quantityNum = parseInt(quantity) || 1;
      const cartLinkWithQuantity = applyButtonLink.replace(/:1\?/, `:${quantityNum}?`);

      // Open the cart link in a new window/tab
      window.open(cartLinkWithQuantity, '_blank');
    } else {
      console.error(new Error('applyButtonLink missing'));
      toast.negative(
        new Error('Unable to continue with the purchase request, please try again later'),
      );
    }
  }, [
    selectedProduct,
    testLocationName,
    userId,
    testLocation,
    agencyCountry,
    isNepalShopifyStoreEnabled,
    isButtonDisabled,
    quantity,
    toast,
    trackEvent,
    testType,
  ]);

  return (
    <Dialog open={open} onOpenChange={setOpen} size="md">
      <Dialog.Trigger>
        <Button
          size={buttonSize}
          intent="primary"
          emphasis="contained"
          width={buttonWidth}
          leadIcon={PlusOutlineIcon}
        >
          {buttonText}
        </Button>
      </Dialog.Trigger>
      <Dialog.Heading>Purchase Test Vouchers</Dialog.Heading>
      <Dialog.Content>
        <Flex direction="column" gap={3}>
          <Text variant="bodyM" intent="secondary" contrast="mid">
            Select your test location and type to purchase vouchers
          </Text>
          <Flex direction="column" gap={4}>
            <Select
              appearance="native"
              aria-label="select-test-location"
              intent="secondary"
              width="fill"
              onChange={(value: string) => setTestLocation(value)}
              value={testLocation}
              label="Test Location"
              placeholder="Select test location"
              disabled={agencyCountry === 'IN'}
            >
              <Select.Option key="placeholder" label="Select test location" value="" disabled />
              {countryOptions.map(country => (
                <Select.Option key={country.value} label={country.label} value={country.value} />
              ))}
            </Select>

            <Box>
              <Select
                appearance="native"
                aria-label="select-test-type"
                intent="secondary"
                width="fill"
                onChange={(value: string) => setTestType(value)}
                value={testType}
                label="Test Type"
                placeholder="Select test type"
              >
                <Select.Option key="placeholder" label="Select test type" value="" disabled />
                {TestVoucherTypes.map((type: string) => (
                  <Select.Option key={type} label={VOUCHER_TYPE_LABEL_MAP[type]} value={type} />
                ))}
              </Select>
            </Box>

            {/* Quantity Input */}
            <Box>
              <Box mb={2}>
                <Text variant="labelM" intent="secondary" contrast="high">
                  Quantity
                </Text>
              </Box>
              <NumberInput
                aria-label="Quantity"
                intent="secondary"
                size="md"
                min={1}
                max={10}
                step={1}
                disabled={isSoldOut}
                valueFormat="integer"
                value={quantity}
                onChange={(value: string) => setQuantity(value)}
                helpText="Number of vouchers to purchase (1-10)"
              />
            </Box>

            {/* Pricing Summary - Only show when we have product and pricing */}
            {selectedProduct && productInfo.pricing && (
              <Notice intent={productInfo.isSoldOut ? 'negative' : 'positive'} width="fill">
                <Flex direction="column" gap={3}>
                  <Flex justify="between" align="start" wrap={true} gap={2}>
                    <Flex direction="column" gap={1} shrink={1}>
                      <Text variant="labelM" intent="secondary" contrast="high">
                        {selectedProduct.productTitle}
                        {productInfo.isSoldOut && ' • Sold Out'}
                      </Text>
                      <Text variant="bodyS" intent="secondary" contrast="mid">
                        {testLocationName} • Quantity: {quantity}
                      </Text>
                    </Flex>
                    <Flex shrink={0}>
                      {productInfo.pricing.hasDiscount ? (
                        <Flex gap={2} align="center">
                          <s>
                            <Text variant="labelM" intent="secondary" contrast="mid">
                              {productInfo.pricing.fullPrice}
                            </Text>
                          </s>
                          <Text
                            variant="labelM"
                            intent={productInfo.isSoldOut ? 'negative' : 'positive'}
                            contrast="high"
                          >
                            {productInfo.pricing.discountedPrice}
                          </Text>
                        </Flex>
                      ) : (
                        <Text
                          variant="labelM"
                          intent={productInfo.isSoldOut ? 'negative' : 'positive'}
                          contrast="high"
                        >
                          {productInfo.pricing.discountedPrice}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                  {/* Disclaimer text - only show when not sold out */}
                  {!productInfo.isSoldOut && (
                    <Text variant="bodyS" intent="secondary" contrast="mid">
                      <strong>Note:</strong> Once a voucher is purchased, it may take up to 5
                      minutes to become available for assigning. Please wait a few minutes to reload
                      the page before attempting to assign the voucher.
                    </Text>
                  )}
                </Flex>
              </Notice>
            )}
          </Flex>
        </Flex>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
        <Button
          intent={!hasRequiredSelections || isSoldOut ? 'secondary' : 'primary'}
          size="md"
          width="fill"
          onClick={() => {
            handleContinue();
            setOpen(false);
          }}
          disabled={!hasRequiredSelections || isButtonDisabled}
        >
          {!hasRequiredSelections ? 'Continue' : isSoldOut ? 'Sold Out' : 'Continue'}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
